import { useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';

import LoanService from '../../../../../../services/LoanService';
import { useUiContext } from '../../../../../../context/UiContext';
import useUserRole from '../../../../../../hooks/useUserRole';

export default function useScheduleActions({
  schedule,
  isScheduleFetching,
  loanId,
  refetch,
  readOnly,
  addQueryParam,
  addNewPayoffInvoiceQueryParam,
}) {
  const { showApiErrorToast, showSuccessToast } = useUiContext();

  const { isBorrower } = useUserRole();

  const canGenerateSetupInvoice =
    !schedule?._invoices?.some((inv) => inv.sequence === 0) &&
    !isScheduleFetching &&
    !isBorrower &&
    !readOnly;

  const canGenerateInvoiceSchedule =
    (schedule?._invoices?.length <= 1 ||
      !schedule?._invoices?.length ||
      !schedule?._invoices) &&
    !isScheduleFetching &&
    !isBorrower &&
    !readOnly;

  const canCreateInvoice =
    !canGenerateInvoiceSchedule && !canGenerateSetupInvoice;

  const { mutate: generateSetupInvoice, isPending: isGeneratingSetupInvoice } =
    useMutation({
      mutationFn: () => LoanService.generateSetupInvoice(loanId),
      onSuccess: () => {
        showSuccessToast('Setup invoice generated successfully');
        refetch();
      },
      onError: (error) => {
        showApiErrorToast(error);
        refetch();
      },
    });

  const {
    mutate: generatePaymentSchedule,
    isPending: isGeneratingPaymentSchedule,
  } = useMutation({
    mutationFn: () => LoanService.generatePaymentSchedule(loanId),
    onSuccess: () => {
      showSuccessToast('Payment schedule generated successfully');
      refetch();
    },
    onError: (error) => {
      showApiErrorToast(error);
      refetch();
    },
  });

  const actions = useMemo(() => {
    if (!schedule) {
      return [];
    }

    return [
      {
        actionTitle: 'Generate setup invoice',
        actionDescription: canGenerateSetupInvoice
          ? 'Generate the first invoice for this loan including initial impounds (if any) and servicing setup fee.'
          : 'A setup invoice already exists for this loan.',
        onClick: generateSetupInvoice,
        disabled: !canGenerateSetupInvoice || isGeneratingSetupInvoice,
      },
      {
        actionTitle: 'Generate invoice schedule',
        actionDescription: canGenerateInvoiceSchedule
          ? 'Generate the full invoice schedule for this loan.'
          : 'This loan is already setup with the full invoice schedule.',
        onClick: generatePaymentSchedule,
        disabled: !canGenerateInvoiceSchedule || isGeneratingPaymentSchedule,
      },
      {
        actionTitle: 'Create invoice',
        actionDescription: canCreateInvoice
          ? 'Create a new invoice for this loan.'
          : 'First generate the setup and invoice schedule.',
        onClick: addQueryParam,
        disabled: !canCreateInvoice,
      },
      {
        actionTitle: 'Create payoff invoice',
        actionDescription: canCreateInvoice
          ? 'Create a payoff invoice for this loan.'
          : 'First generate the setup and invoice schedule.',
        onClick: addNewPayoffInvoiceQueryParam,
        disabled: !canCreateInvoice,
      },
    ];
  }, [
    addNewPayoffInvoiceQueryParam,
    addQueryParam,
    canCreateInvoice,
    canGenerateInvoiceSchedule,
    canGenerateSetupInvoice,
    generatePaymentSchedule,
    generateSetupInvoice,
    isGeneratingPaymentSchedule,
    isGeneratingSetupInvoice,
    schedule,
  ]);

  return {
    actions,
    isLoading: isGeneratingSetupInvoice || isGeneratingPaymentSchedule,
  };
}
